import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AutoFillable, AutoFiller } from '@wz-decorators/auto-form-filler';
import { TextInputComponent } from '@wz-forms';
import { AutoUnsubscribeComponent } from '@wz-utilities/auto-unsubscribe.component';
import { formatDate, getDateUtc } from '@wz-utilities/date.utilities';
import { addDays } from 'date-fns';
import { HealthModels } from 'models/health.models';
import { PolicyModels } from 'models/policy.models';
import { QuotationModels } from 'models/quotation.models';
import { Observable, of } from 'rxjs';
import { HealthAssureFormComponentB2C } from '../health-assure-formB2C/health-assure-form.component';
import { HealthFamilyFormComponent } from '../health-family-form/health-family-form.component';

@AutoFillable()
@Component({
  selector: 'wz-health-quotation-formB2C',
  templateUrl: './health-quotation-form.component.html',
  styleUrls: ['./health-quotation-form.component.scss'],
  standalone: false,
})
export class HealthQuotationFormComponentB2C
  extends AutoUnsubscribeComponent
  implements OnInit, OnDestroy
{
  @ViewChildren(TextInputComponent)
  inputs = new QueryList<TextInputComponent>();
  @ViewChild(HealthAssureFormComponentB2C, { static: true })
  healthAssureComponent!: HealthAssureFormComponentB2C;
  @ViewChild(HealthFamilyFormComponent, { static: true })
  healthFamilyComponent!: HealthFamilyFormComponent;

  @Input() disabled = false;

  @Input() package!: PolicyModels.PackageEnum;

  // Data
  public regimeSocial!: HealthModels.RegimeSocialEnum;
  public dateEffet!: Date;
  public formula!: QuotationModels.FormulaEnum;

  private _quotationForm!: HealthModels.QuotationForm;
  get quotationForm() {
    return this._quotationForm;
  }

  @Input()
  set quotationForm(risk: HealthModels.QuotationForm) {
    this._quotationForm = risk;
    if (risk && this.formGroup) {
      this.patchValue(risk);
    }
  }

  public formGroup!: FormGroup;
  hide = true;

  constructor(private fb: FormBuilder) {
    super();
  }

  ngOnInit() {
    this.formGroup = this.createForm();

    if (this._quotationForm) {
      this.patchValue(this._quotationForm);
    }
  }

  public patchValue(quotationForm: HealthModels.QuotationForm) {
    this.formGroup.patchValue(quotationForm);

    this.healthAssureComponent.onPatchValue(quotationForm.assurePrincipal, {
      formula: quotationForm.universBesoinInfo.formula,
    });

    this.healthFamilyComponent.onPatchValue(quotationForm.family, {
      dateEffet:
        getDateUtc(quotationForm.assurePrincipal.dateEffet) ?? new Date(),
      formula: quotationForm.universBesoinInfo.formula,
    });
  }

  private createForm() {
    const grp = this.fb.group({
      assurePrincipal: this.healthAssureComponent.formGroup,
      family: this.healthFamilyComponent.formGroup,
    });

    return grp;
  }

  newRegimeSocial(regimeSocial: HealthModels.RegimeSocialEnum) {
    this.regimeSocial = regimeSocial;
  }

  newDateEffet(dateEffet: any) {
    this.dateEffet = dateEffet;
  }

  //Cette methode n'est plus utilisée, toutes les infos venant de healthAssureForm sont dans dataStoredQuotation
  submit(): Observable<HealthModels.QuotationForm> {
    const rawValue: any = <HealthModels.QuotationForm>(
      this.formGroup.getRawValue()
    );
    rawValue.vadInfo.isVad =
      rawValue.vadInfo.saleType == HealthModels.SaleTypeEnum.Remote;
    rawValue.vadInfo.tarifier = 'oui_Init';

    const gclid = sessionStorage.getItem('gclid');
    rawValue.vadInfo.gclid = gclid;
    const msclkid = sessionStorage.getItem('msclkid');
    rawValue.vadInfo.msclkid = msclkid;
    // // if (this.formGroup.valid)
    {
      return of(rawValue);
    }
    // //else
    //  {
    //    markAllControlsAsDirty(this.formGroup, true);
    //    const errors = this.inputs.filter(i => i.errorState);
    //    if (errors && errors.length > 0) {
    //      errors[0].focus();
    //    }

    //    return observableThrowError('Form invalid');
    //  }
  }

  @Output() submitEvent = new EventEmitter();
  public dataStoredQuotation!: HealthModels.HealthQuotationStoredData;
  //Recoit dataStoredQuotation venant de HealthAssureForm, puis envoyéé vers HealthInsuranceQuotation
  receiveSubmit(event: any) {
    // debugger; // utile
    if (event) this.dataStoredQuotation = event;
    this.submitEvent.emit();
  }

  @AutoFiller()
  fillFake() {
    this.quotationForm = {
      assurePrincipal: {
        dateEffet: formatDate(addDays(getDateUtc() ?? new Date(), 1)),
        dateNaissance: '10/04/1978',
        groupVille: {
          codePostal: '75001',
          inseeCode: '75101',
          ville: 'Paris 01',
        },
        regimeSocial: HealthModels.RegimeSocialEnum.AlsaceMoselle,
      },
      vadInfo: {
        isVad: true,
        saleType: HealthModels.SaleTypeEnum.Remote,
        isCallFromProspect: false,
        isRappel: false,
        isBloctel: true,
        toggleBloctel: true,
        toggleVad: true,
      },
      family: {
        conjoint: {
          dateNaissance: '15/12/1960',
          regimeSocial: HealthModels.RegimeSocialEnum.General,
          isRegimeSocialDifferent: true,
          isDisabled: false,
        },
        enfants: [
          {
            dateNaissance: '19/01/2017',
            regimeSocial: HealthModels.RegimeSocialEnum.General,
            isRegimeSocialDifferent: true,
            isDisabled: false,
          },
        ],
      },
      universBesoinInfo: {
        formula: QuotationModels.FormulaEnum.Sante2,
        hasAlreadyHealthContract: true,
        healthContractName: 'Axa',
        monthlyBudget: 100,
        questions: [
          {
            type: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
            level: HealthModels.UniversBesoinLevelEnum.Medium,
          },
          {
            type: HealthModels.UniversBesoinTypeEnum.Medecine,
            level: HealthModels.UniversBesoinLevelEnum.Medium,
          },
          {
            type: HealthModels.UniversBesoinTypeEnum.Pharmacie,
            level: HealthModels.UniversBesoinLevelEnum.Medium,
          },
          {
            type: HealthModels.UniversBesoinTypeEnum.Dentaire,
            level: HealthModels.UniversBesoinLevelEnum.High,
          },
          {
            type: HealthModels.UniversBesoinTypeEnum.Optique,
            level: HealthModels.UniversBesoinLevelEnum.Medium,
          },
          {
            type: HealthModels.UniversBesoinTypeEnum.Auditif,
            level: HealthModels.UniversBesoinLevelEnum.Low,
          },
          {
            type: HealthModels.UniversBesoinTypeEnum.BienEtre,
            level: HealthModels.UniversBesoinLevelEnum.Medium,
          },
          {
            type: HealthModels.UniversBesoinTypeEnum.AvantageCapitalDC,
            level: HealthModels.UniversBesoinLevelEnum.Medium,
          },
        ],
      },
    };
  }
}
